import React, { memo, ReactNode, cloneElement } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { DialogProps } from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { TypographyVariant } from '@mui/material';

import ModalFormTitle from '../../Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle/ModalFormTitle';
import ActionButtons from '../ActionButtons';
import { DraggableDialog } from '../../DraggablePaper/DraggablePaper';

import generalDialogFormStyles from './generalDialogFormStyles';
import { FormResetOnClose } from './FormResetOnClose';

type Props<T, TValue = any extends object ? T : any> = {
    onSubmit: (values: TValue | any, actions?: FormikHelpers<TValue>) => void;
    onClose: () => void;
    open: boolean;
    initialValues: TValue;
    children: ReactNode;
    title?: string;
    validationSchema?: object;
    maxWidth?: DialogProps['maxWidth'];
    PaperProps?: DialogProps['PaperProps'];
    titleVariant?: TypographyVariant;
    dialogContentClasses?: object;
    minHeight?: string;
    hasCancelButton: boolean;
    actionText: string;
    cancelText?: string;
    hasSecondAction?: boolean;
    secondActionText?: string;
    setActionSubmitState?: React.Dispatch<React.SetStateAction<string>>;
};

function DialogForm<TValue = object>({
    onSubmit,
    onClose,
    open,
    title = '',
    initialValues,
    validationSchema,
    maxWidth = 'md',
    PaperProps,
    children,
    titleVariant,
    dialogContentClasses,
    minHeight = '50vh',
    hasCancelButton,
    actionText,
    cancelText = 'Cancel',
    hasSecondAction = false,
    secondActionText,
    setActionSubmitState
}: Props<TValue>) {
    const classes = generalDialogFormStyles({ minHeight });

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            {(formProps) => {
                return (
                    <>
                        <FormResetOnClose open={open} formProps={formProps} initialValues={initialValues} />
                        <Form className={!open ? classes.hiddenForm : ''}>
                            <DraggableDialog
                                classes={{ paper: classes.minHeight50 }}
                                open={open}
                                onClose={onClose}
                                fullWidth
                                maxWidth={maxWidth}
                                PaperProps={PaperProps}
                                disableBackdropClick
                            >
                                <ModalFormTitle title={title} toggleDialog={onClose} variant={titleVariant} />
                                <DialogContent classes={dialogContentClasses}>
                                    {React.Children.map(children, (child: any) => {
                                        return cloneElement(child, { formProps });
                                    })}
                                </DialogContent>
                                <ActionButtons
                                    onClose={onClose}
                                    hasCancelButton={hasCancelButton}
                                    actionText={actionText}
                                    cancelText={cancelText}
                                    hasSecondAction={hasSecondAction}
                                    secondActionText={secondActionText}
                                    setActionSubmitState={setActionSubmitState}
                                />
                            </DraggableDialog>
                        </Form>
                    </>
                );
            }}
        </Formik>
    );
}

export default memo(DialogForm);
